import { useEffect, useState, useRef } from "react";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000";

// Read the ?error=... param synchronously at module load. Hoisting this out of
// the effect lets us seed initial state from it with lazy initializers, which
// eliminates the synchronous setState-in-effect that was causing a visible
// "processing..." hang when the effect race-condition flipped the flag the
// wrong way on StrictMode double-invocation.
function readInitialErrorFromURL(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("error");
  return raw ? decodeURIComponent(raw) : null;
}

export function SAMLCallbackPage() {
  // useState with a lazy initializer runs the URL read exactly once, at mount,
  // and keeps the value stable across re-renders without touching a ref during
  // render (which the react-hooks/refs rule correctly flags).
  const [initialError] = useState<string | null>(readInitialErrorFromURL);
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    () => (initialError ? "error" : "processing")
  );
  const [errorMessage, setErrorMessage] = useState<string>(() => initialError ?? "");
  const hasVerified = useRef(false);

  useEffect(() => {
    // Bail early if we already initialized in error state from the URL, or if
    // StrictMode already invoked us once.
    if (initialError !== null || hasVerified.current) {
      return;
    }
    hasVerified.current = true;

    // Single fetch to verify auth. setState calls happen inside Promise
    // callbacks (not synchronously in the effect body), which is the pattern
    // react-hooks/set-state-in-effect explicitly allows.
    fetch(`${API_URL}/auth/verify`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Authentication failed");
        }
        return response.json();
      })
      .then(() => {
        setStatus("success");
        // Hard redirect — avoid client-side router so a stale React tree
        // doesn't repaint the processing screen after navigation.
        setTimeout(() => {
          window.location.href = "/";
        }, 500);
      })
      .catch((err) => {
        setStatus("error");
        setErrorMessage(err.message || "Failed to verify authentication");
      });
  }, [initialError]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          {status === "processing" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
              <h2 className="mt-6 text-3xl font-extrabold text-foreground">
                Completing sign in...
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Please wait while we authenticate your credentials
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-700 dark:text-green-300" />
              <h2 className="mt-6 text-3xl font-extrabold text-foreground">
                Success!
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Redirecting to dashboard...
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
              <h2 className="mt-6 text-3xl font-extrabold text-foreground">
                Authentication Failed
              </h2>
              <div className="mt-4 rounded-md bg-destructive/10 border border-destructive/20 p-4">
                <p className="text-sm text-destructive">{errorMessage}</p>
              </div>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => { window.location.href = "/admin/login"; }}
                  className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90"
                >
                  Try again
                </button>
                <button
                  onClick={() => { window.location.href = "/login"; }}
                  className="w-full py-2 px-4 border border-border rounded-md shadow-sm text-sm font-medium text-foreground bg-card hover:bg-accent"
                >
                  Customer login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
